import { FC, useState } from "react";
import {
  UploadItem,
  UploadItemType,
  UploadItemStatus,
} from "./types/upload-item";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { ChevronDown, ChevronUp, Folder, X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import FileUploadItem from "./FileUploadItem";

type Props = {
  folder: UploadItem & { children?: UploadItem[] };
  level?: number;
};

const FolderUploadItem: FC<Props> = ({ folder, level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = folder.children && folder.children.length > 0;
  const folderName = folder.name.split("/").pop() || folder.name;
  const MAX_INDENT_LEVEL = 4;
  const shouldIndent = level < MAX_INDENT_LEVEL;

  return (
    <li className="space-y-1">
      <div className="group flex items-center gap-1.5 p-1.5 sm:p-2 rounded-md hover:bg-accent/50 transition-colors">
        {hasChildren && (
          <Button
            variant="ghost"
            size="icon"
            className="size-4 p-0 shrink-0"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronDown className="size-3" />
            ) : (
              <ChevronUp className="size-3" />
            )}
          </Button>
        )}
        {!hasChildren && <div className="size-4 shrink-0" />}
        <Folder className="size-4 text-muted-foreground group-hover:text-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-xs sm:text-sm font-medium truncate cursor-default">
                {folderName}
              </p>
            </TooltipTrigger>
            {folderName.length > 15 && (
              <TooltipContent side="top" className="max-w-[250px]">
                <p className="break-words text-xs">{folder.name}</p>
              </TooltipContent>
            )}
          </Tooltip>
          <p className="text-xs text-muted-foreground group-hover:text-foreground truncate">
            {folder.status === UploadItemStatus.Pending && "Opretter mappe..."}
            {folder.status === UploadItemStatus.Processing && "Behandler..."}
            {folder.status === UploadItemStatus.Completed && "Mappe oprettet"}
            {folder.status === UploadItemStatus.Failed && "Fejl ved oprettelse"}
          </p>
        </div>
        {folder.status !== UploadItemStatus.Completed &&
          folder.status !== UploadItemStatus.Failed && (
            <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
          )}
        {folder.status === UploadItemStatus.Completed && (
          <div className="size-4 rounded-full bg-success flex items-center justify-center shrink-0">
            <svg
              className="size-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
        {folder.status === UploadItemStatus.Failed && (
          <div className="size-4 rounded-full bg-destructive flex items-center justify-center shrink-0">
            <X className="size-3 text-white" />
          </div>
        )}
      </div>

      {isExpanded && hasChildren && (
        <ul
          className="space-y-1 overflow-visible"
          style={{
            marginLeft: shouldIndent ? "0.5rem" : "0",
            paddingLeft: shouldIndent ? "0.375rem" : "0",
            borderLeft: shouldIndent ? "1px solid hsl(var(--border))" : "none",
          }}
        >
          {folder.children!.map((child) => {
            if (child.type === UploadItemType.FOLDER) {
              return (
                <FolderUploadItem
                  key={child.id}
                  folder={child}
                  level={level + 1}
                />
              );
            }
            return (
              <li key={child.id}>
                <FileUploadItem uploadItem={child} />
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

export default FolderUploadItem;
